import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError, take } from 'rxjs/operators';
import { LoginService } from 'src/app/services/login.service';
import { environment } from 'src/environments/environment';
import { IndicationOutputDTO } from '../contracts/indication';
import {
  AreaOfExpertiseOutputDTO,
  IndicationAreaOfExpertiseWithBenefitsOutputDTO,
} from '../contracts/area-of-expertise';

export type SchedulePriceResponse = {
  id: string;
  planId: number;
  specialtyId: number;
  indicationAreaOfExpertiseId: string;
  serviceFee: number;
  telemedicineFee: number;
  amount: number;
  createdAt: number;
  updatedAt: number;
};

type IndicationResponse = {
  status: boolean;
  message: string;
  data: IndicationOutputDTO;
};

const INVALID_ID_VALUES = new Set(['', 'undefined', 'null']);

@Injectable({
  providedIn: 'root',
})
export class IndicationServiceV2 {
  private static readonly EMPTY_AREA: IndicationAreaOfExpertiseWithBenefitsOutputDTO = {
    id: '',
    name: '',
    showServices: false,
    indicationBenefit: [],
  };

  private readonly urlV1 = environment.api;
  private readonly urlV2 = `${environment.api_v2}/v2`;
  private readonly http = inject(HttpClient);
  private readonly loginService = inject(LoginService);

  public getById(indicationId: number): Observable<IndicationResponse> {
    return this.http.get<IndicationResponse>(`${this.urlV1}/indications/${indicationId}`);
  }

  /**
   * Áreas previdenciárias (`indication_area_of_expertise`) para o resumo da indicação.
   * Não confundir com `GET .../indication-area-of-expertise` (especialidades do plano para agendamento).
   */
  public getPracticeAreas(): Observable<AreaOfExpertiseOutputDTO[]> {
    return this.http.get<AreaOfExpertiseOutputDTO[]>(
      `${this.urlV2}/indication-area-of-expertise/for-indication-summary`
    );
  }

  /**
   * Busca benefícios previdenciários por área de atuação (UUID).
   * Retorna área vazia se o ID for inválido (null, undefined, vazio ou string literal).
   * 
   * @param areaId UUID da área de atuação previdenciária
   * @returns Observable com área e seus benefícios vinculados
   */
  public getBenefitsByAreaId(
    areaId: string | null | undefined
  ): Observable<IndicationAreaOfExpertiseWithBenefitsOutputDTO> {
    const normalizedId = areaId?.trim();
    
    if (!this.isValidAreaId(normalizedId)) {
      return of(IndicationServiceV2.EMPTY_AREA);
    }

    return this.http.get<IndicationAreaOfExpertiseWithBenefitsOutputDTO>(
      this.buildAreaBenefitsUrl(normalizedId)
    );
  }

  private isValidAreaId(id: string | null | undefined): id is string {
    return id != null && id.length > 0 && !INVALID_ID_VALUES.has(id);
  }

  private buildAreaBenefitsUrl(areaId: string): string {
    return `${this.urlV2}/indication-area-of-expertise/by-uuid/${encodeURIComponent(areaId)}`;
  }

  public getIndicationDetailsByIndicationId(indicationId: number): Observable<unknown> {
    return this.http.get(`${this.urlV2}/indication/${indicationId}`);
  }

  public getSchedulePrice(indicationId: number): Observable<SchedulePriceResponse> {
    return this.http
      .get<SchedulePriceResponse>(`${this.urlV2}/indication/${indicationId}/schedule-price`)
      .pipe(
        take(1),
        catchError((error) => {
          this.loginService.showMessage('Não foi possível buscar o preço da consulta!');
          return throwError(() => error);
        })
      );
  }
}
